import AuthApi, {
	type RequestPasswordResetParams,
	type ResetPasswordParams,
} from '@/helpers/api/AuthApi'
import type { User } from '@/models/User/Model'
import { defineStore } from 'pinia'
import { usePostHog } from '@/composables/usePostHog'

const auth = new AuthApi()
const { posthog } = usePostHog()

const useAuthStore = defineStore('auth', {
	state: () => {
		return {
			user: null as User | null,
			userReturned: false,
			userRequest: null as Promise<any> | null,
		}
	},
	getters: {
		activeFeatures: (state): string[] => {
			const flags: Record<string, boolean> = state.user?.feature_flags ?? {}
			return Object.keys(flags).filter((key) => flags[key])
		},
	},
	actions: {
		async login({
			email,
			password,
			remember = false,
		}: {
			email: string
			password: string
			remember?: boolean
		}) {
			await auth.login({ email, password, remember })
			await this.getUser()
		},
		async logout() {
			posthog.reset()
			await auth.logout()
			this.user = null
		},
		async getUser() {
			try {
				if (!this.userRequest) {
					this.userRequest = auth.getUser()
				}
				this.user = (await this.userRequest).data
				posthog.identify(String(this.user!.id), {
					email: this.user!.email,
				})
			} catch (error: any) {
				if (error.response?.status !== 401) throw error
			} finally {
				this.userRequest = null
			}
			this.userReturned = true
		},
		async register(params: {
			email: string
			password: string
			password_confirmation: string
			accepts_toc: boolean
		}) {
			await auth.register(params)
		},
		githubLogin() {
			auth.githubLoginRedirect()
		},
		googleLogin() {
			auth.googleLoginRedirect()
		},
		async requestPasswordReset(params: RequestPasswordResetParams) {
			return await auth.requestPasswordReset(params)
		},
		async resetPassword(params: ResetPasswordParams) {
			return await auth.resetPassword(params)
		},
		hasFeatureFlag(flag: string): boolean {
			return !!this.user?.feature_flags[flag]
		},
	},
})

export { useAuthStore }
